import { useState } from "react";
import { DateTime } from "luxon";
import { FaChevronDown, FaChevronUp, FaInbox } from "react-icons/fa";
import PecDocumentViewer from "./components/PecDocumentViewer.tsx";
import { trpc } from "../main/components/trpc.ts";

function getStatusChipClassName(status: string) {
  switch (status) {
    case "completed":
      return "border-green-200 bg-green-50 text-green-700";
    case "failed":
      return "border-red-200 bg-red-50 text-red-700";
    case "not_applicable":
      return "border-slate-200 bg-slate-50 text-slate-700";
    default:
      return "border-blue-200 bg-blue-50 text-blue-700";
  }
}

function formatOcrStatus(status: string) {
  return status === "not_applicable" ? "OCR non requis" : status;
}

const PecStagingPage = () => {
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);
  const query = trpc.pec.listPecDocuments.useQuery({ stagingOnly: true });

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4 p-4">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <FaInbox className="text-blue-600" />
              PEC non rattachées
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Rattacher les PEC uploadées sans patient, relire le PDF et corriger les champs OCR.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-600">
            {query.data?.length ?? 0} document(s)
          </span>
        </div>
      </div>

      {query.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          Chargement des PEC staging...
        </div>
      ) : query.error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-sm">
          {query.error.message || "Impossible de charger les PEC staging."}
        </div>
      ) : !query.data || query.data.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500 shadow-sm">
          Aucune PEC staging en attente de rattachement.
        </div>
      ) : (
        <div className="space-y-4">
          {query.data.map((document) => {
            const isExpanded = expandedDocumentId === document.id;

            return (
              <div
                key={document.id}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedDocumentId((current) =>
                      current === document.id ? null : document.id,
                    )
                  }
                  className="flex w-full items-center justify-between gap-3 bg-slate-50 px-4 py-4 text-left transition-colors hover:bg-slate-100"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">
                        {document.documentType === "coverage_request"
                          ? "Demande de prise en charge"
                          : document.documentType === "coverage_response"
                            ? "Prise en charge"
                            : "Document PEC"}
                      </span>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusChipClassName(document.ocrStatus)}`}
                      >
                        {formatOcrStatus(document.ocrStatus)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {document.network} · capturé le {DateTime.fromISO(document.capturedAt).toFormat("dd/MM/yyyy HH:mm")}
                    </p>
                    {(document.patientLastName || document.patientFirstName) && (
                      <p className="mt-1 text-sm text-slate-600">
                        OCR patient : {[document.patientFirstName, document.patientLastName]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    )}
                  </div>
                  {isExpanded ? (
                    <FaChevronUp className="text-slate-500" />
                  ) : (
                    <FaChevronDown className="text-slate-500" />
                  )}
                </button>

                {isExpanded ? (
                  <div className="border-t border-slate-200 p-4">
                    <PecDocumentViewer
                      documentId={document.id}
                      allowAssignment
                      onAssigned={() => setExpandedDocumentId(null)}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PecStagingPage;
